import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import {
  Camera,
  Image as ImageIcon,
  RotateCcw,
  ScanLine,
  Sparkles,
  Upload,
  X,
} from 'lucide-react';
import { supabase } from '../lib/supabase';

/**
 * VIN photo scanner — same capture UX as the business card scanner (mobile
 * gets an instant full-screen viewfinder, desktop gets take/upload buttons).
 * AI reads the 17-character VIN from the photo (normal AI-token billing via
 * the ai-scan-vin edge function); the caller then runs the vehicle decode.
 */
interface VinScanModalProps {
  isOpen: boolean;
  businessId: string;
  onClose: () => void;
  /** Fired with the validated 17-character VIN; the modal closes itself. */
  onScanned: (vin: string) => void;
}

type Step = 'capture' | 'processing';

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function VinScanModal({
  isOpen,
  businessId,
  onClose,
  onScanned,
}: VinScanModalProps) {
  const [step, setStep] = useState<Step>('capture');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  // Mobile: full-screen live viewfinder that opens instantly.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 640,
  );
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const liveStreamRef = useRef<MediaStream | null>(null);
  const [isCameraStarting, setIsCameraStarting] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [isShutterFlashing, setIsShutterFlashing] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const shouldShowLiveCamera = isOpen && isMobile;

  const stopLiveCamera = useCallback(() => {
    const stream = liveStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      liveStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startLiveCamera = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera is not available in this browser');
      return;
    }
    setIsCameraStarting(true);
    setCameraError(null);
    try {
      stopLiveCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      liveStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch {
          // Some browsers reject play() before user gesture; the stream is
          // still active and will render once the user interacts.
        }
      }
    } catch (err) {
      console.error('Camera error:', err);
      const message =
        err instanceof Error && err.name === 'NotAllowedError'
          ? 'Camera permission denied. Enable camera access in Settings to scan VINs.'
          : 'Could not access the camera on this device.';
      setCameraError(message);
    } finally {
      setIsCameraStarting(false);
    }
  }, [facingMode, stopLiveCamera]);

  useEffect(() => {
    if (shouldShowLiveCamera && step === 'capture') {
      void startLiveCamera();
    } else {
      stopLiveCamera();
    }
    return () => {
      stopLiveCamera();
    };
  }, [shouldShowLiveCamera, step, startLiveCamera, stopLiveCamera]);

  useEffect(() => {
    if (!isOpen) {
      setStep('capture');
      setError(null);
      setCameraError(null);
      setPreviewUrl((url) => {
        if (url) URL.revokeObjectURL(url);
        return null;
      });
    }
  }, [isOpen]);

  const handleFileSelected = async (file: File | null) => {
    if (!file) return;
    setError(null);
    stopLiveCamera();
    setPreviewUrl((url) => {
      if (url) URL.revokeObjectURL(url);
      return URL.createObjectURL(file);
    });
    setStep('processing');

    try {
      const imageBase64 = await fileToBase64(file);
      const { data, error: fnError } = await supabase.functions.invoke('ai-scan-vin', {
        body: { businessId, imageBase64, imageMimeType: file.type || 'image/jpeg' },
      });
      if (fnError) throw fnError;
      if (data?.insufficientTokens) {
        throw new Error(data.message || 'Insufficient AI tokens to scan the VIN.');
      }
      if (!data?.success || !data?.data?.vin) {
        throw new Error(data?.error || 'Could not read a VIN in that photo.');
      }

      onScanned(String(data.data.vin));
      onClose();
    } catch (err) {
      console.error('VIN scan failed:', err);
      const message = err instanceof Error ? err.message : 'Failed to scan the VIN.';
      setStep('capture');
      if (isMobile) {
        // The live camera view has no inline error area — surface via toast.
        toast.error(message);
      } else {
        setError(message);
      }
    }
  };

  /** Capture the current viewfinder frame as a JPEG File and feed it into the scan. */
  const handleSnapPhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
      toast.error('Camera is not ready yet');
      return;
    }
    setIsShutterFlashing(true);
    setTimeout(() => setIsShutterFlashing(false), 180);

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          toast.error('Could not capture photo');
          return;
        }
        const file = new File([blob], `vin-${Date.now()}.jpg`, { type: 'image/jpeg' });
        void handleFileSelected(file);
      },
      'image/jpeg',
      0.92,
    );
  };

  if (!isOpen) return null;

  // Mobile capture: full-screen live viewfinder with a wide VIN framing strip.
  const liveCameraContent = (
    <div
      className="fixed inset-0 bg-black"
      style={{
        zIndex: 100550,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Hidden file input for photo library picker (no capture attr) */}
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          void handleFileSelected(e.target.files?.[0] ?? null);
          e.target.value = '';
        }}
      />

      {cameraError ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 text-white">
          <Camera className="h-12 w-12 mb-4 text-white/70" />
          <p className="text-base font-semibold mb-1">Camera unavailable</p>
          <p className="text-sm text-white/70 mb-6 max-w-xs">{cameraError}</p>
          <div className="flex flex-col gap-2 w-full max-w-xs">
            <button
              type="button"
              onClick={() => void startLiveCamera()}
              className="px-4 py-3 bg-white text-black rounded-xl font-semibold text-sm active:scale-[0.98] transition-transform"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => uploadInputRef.current?.click()}
              className="px-4 py-3 bg-white/15 text-white rounded-xl font-medium text-sm active:scale-[0.98] transition-transform"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Choose from photos
            </button>
          </div>
        </div>
      ) : step === 'processing' ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 text-white">
          {previewUrl && (
            <img
              src={previewUrl}
              alt="VIN"
              className="max-h-48 rounded-xl shadow-md mb-6 object-contain"
            />
          )}
          <div className="flex items-center gap-2 font-semibold">
            <Sparkles className="h-5 w-5 animate-pulse" />
            Reading the VIN…
          </div>
        </div>
      ) : (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          playsInline
          muted
        />
      )}

      {/* Off-screen canvas for capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Shutter flash overlay */}
      <div
        className="pointer-events-none absolute inset-0 bg-white transition-opacity duration-150"
        style={{ opacity: isShutterFlashing ? 0.9 : 0 }}
      />

      {/* Top bar */}
      <div
        className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0))',
        }}
      >
        <button
          type="button"
          onClick={() => {
            stopLiveCamera();
            onClose();
          }}
          className="h-10 w-10 flex items-center justify-center rounded-full bg-black/40 text-white active:scale-[0.95] transition-transform"
          aria-label="Close"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-2 text-white">
          <ScanLine className="h-4 w-4 opacity-80" />
          <span className="text-sm font-semibold tracking-tight">Scan VIN</span>
        </div>

        <button
          type="button"
          onClick={() => setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'))}
          disabled={isCameraStarting || !!cameraError || step === 'processing'}
          className="h-10 w-10 flex items-center justify-center rounded-full bg-black/40 text-white active:scale-[0.95] transition-transform disabled:opacity-40"
          aria-label="Flip camera"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <RotateCcw className="h-5 w-5" />
        </button>
      </div>

      {/* Framing hint — wide strip matching a VIN plate/sticker */}
      {!cameraError && step === 'capture' && (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          <div
            className="rounded-xl border-2 border-white/70"
            style={{
              width: 'min(88vw, 460px)',
              height: 'min(16vw, 84px)',
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.18)',
            }}
          />
        </div>
      )}
      {!cameraError && step === 'capture' && (
        <div
          className="absolute left-0 right-0 flex justify-center pointer-events-none"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 64px)' }}
        >
          <span className="px-3 py-1 rounded-full bg-black/45 text-white text-[12px] font-medium tracking-tight">
            {isCameraStarting
              ? 'Starting camera…'
              : 'Line up the VIN inside the frame'}
          </span>
        </div>
      )}

      {/* Bottom controls: photo library in the corner + shutter */}
      {step === 'capture' && (
        <div
          className="absolute bottom-0 left-0 right-0"
          style={{
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
            background: 'linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0))',
          }}
        >
          <div className="flex items-center justify-between px-8 pt-10">
            {/* Photo library */}
            <button
              type="button"
              onClick={() => uploadInputRef.current?.click()}
              className="h-14 w-14 flex items-center justify-center rounded-2xl bg-white/15 text-white active:scale-[0.94] transition-transform"
              aria-label="Choose from photos"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <ImageIcon className="h-7 w-7" />
            </button>

            {/* Shutter */}
            <button
              type="button"
              onClick={handleSnapPhoto}
              disabled={!!cameraError || isCameraStarting}
              className="relative h-20 w-20 rounded-full flex items-center justify-center active:scale-[0.96] transition-transform disabled:opacity-50"
              aria-label="Take photo"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <span className="absolute inset-0 rounded-full border-[3px] border-white" />
              <span className="h-[60px] w-[60px] rounded-full bg-white" />
            </button>

            {/* Spacer to keep the shutter centered */}
            <div className="h-14 w-14" />
          </div>
        </div>
      )}
    </div>
  );

  // Desktop: simple take/upload panel.
  const panelContent = (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{
        zIndex: 100550,
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={(e) => {
        // Portal clicks bubble through the React tree to the parent modal's
        // own close-on-backdrop handler — keep them contained here.
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-blue-50 flex items-center justify-center">
              <ScanLine className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                Scan VIN
              </h2>
              <p className="text-xs text-gray-500">AI reads the VIN from a photo</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-95 transition-all"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error && (
            <div className="mb-4 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700">
              {error}
            </div>
          )}

          {step === 'capture' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Take a photo of the VIN — the windshield plate, door-jamb sticker,
                or registration — and the AI will read all 17 characters.
              </p>
              <button
                onClick={() => cameraInputRef.current?.click()}
                className="w-full flex items-center gap-3 px-4 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-semibold transition-all active:scale-[0.98]"
                style={{ boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)' }}
              >
                <Camera className="h-5 w-5" />
                Take Photo
              </button>
              <button
                onClick={() => uploadInputRef.current?.click()}
                className="w-full flex items-center gap-3 px-4 py-4 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-2xl font-semibold transition-all active:scale-[0.98]"
              >
                <Upload className="h-5 w-5" />
                Upload Image
              </button>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  void handleFileSelected(e.target.files?.[0] ?? null);
                  e.target.value = '';
                }}
              />
              <input
                ref={uploadInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  void handleFileSelected(e.target.files?.[0] ?? null);
                  e.target.value = '';
                }}
              />
            </div>
          )}

          {step === 'processing' && (
            <div className="py-10 flex flex-col items-center text-center">
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt="VIN"
                  className="max-h-40 rounded-xl shadow-md mb-5 object-contain"
                />
              )}
              <div className="flex items-center gap-2 text-blue-600 font-semibold">
                <Sparkles className="h-5 w-5 animate-pulse" />
                Reading the VIN…
              </div>
              <p className="text-sm text-gray-500 mt-1">Extracting all 17 characters</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(shouldShowLiveCamera ? liveCameraContent : panelContent, document.body);
}
